/**
 * Web Push on the browser side.
 *
 * The pieces that differ between browsers live here:
 *
 *   - Chrome and Firefox both require the permission request to happen
 *     inside a user gesture, so `requestPermission()` is only ever called
 *     from the settings toggle's click handler — never on mount.
 *   - Firefox for Android has no Push API, so `subscribe()` fails and the
 *     toggle has to say so rather than pretending it worked.
 *   - The subscription's `applicationServerKey` is base64url and has to be
 *     handed to `subscribe()` as bytes.
 */

import { api } from '@/lib/api'

/** Where the service worker lives. It must be at the root to cover all URLs. */
const SW_PATH = '/sw.js'

export type PermissionState = 'granted' | 'denied' | 'default' | 'unsupported'

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** The browser's current permission state. */
export function permissionState(): PermissionState {
  if (!isPushSupported()) return 'unsupported'
  return Notification.permission as PermissionState
}

/** Register the service worker, returning its registration. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH)
  if (existing) return existing
  return navigator.serviceWorker.register(SW_PATH, { scope: '/' })
}

/**
 * Base64url → bytes, for `applicationServerKey`.
 *
 * The buffer type is named so it satisfies `BufferSource`, which wants a
 * view over a concrete `ArrayBuffer`.
 *
 * A key that is not base64url is a server-side mistake, and the raw
 * `atob` exception says nothing useful about it — "The string to be decoded
 * is not correctly encoded" tells the user nothing about which value is
 * wrong or who has to fix it. So the check is done here, and the error
 * names the cause.
 */
function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const trimmed = value.trim()
  if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
    throw new Error(
      'This server sent an invalid push key. The VAPID secret on the server is misconfigured.',
    )
  }

  const padded = trimmed.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/**
 * Ask for permission, subscribe and register the subscription with the
 * server. Must be called from a user gesture.
 *
 * Returns `false` when the user declined or the browser cannot push.
 */
export async function enablePush(vapidPublicKey: string): Promise<boolean> {
  if (!isPushSupported()) return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const registration = await registerServiceWorker()
  // The subscription needs a controller, so wait for the SW to be ready.
  await navigator.serviceWorker.ready

  const applicationServerKey = base64UrlToBytes(vapidPublicKey)
  const subscription = await subscribeWithKey(registration, applicationServerKey)

  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false

  await api('/api/v1/me/push/subscriptions', {
    method: 'POST',
    body: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
  })

  return true
}

/**
 * The subscription for this app, created if there is none.
 *
 * An existing subscription is only reusable while it was made for the same
 * application server key. The push services tie a subscription to the key it
 * was created with, so after the key rotates — or if it was ever misconfigured
 * — the old subscription is dead weight: pushes to it are rejected, and
 * reusing it looks enabled while nothing arrives. So the key is compared, and
 * a mismatch means a fresh subscription.
 */
async function subscribeWithKey(
  registration: ServiceWorkerRegistration,
  applicationServerKey: Uint8Array<ArrayBuffer>,
): Promise<PushSubscription> {
  const existing = await registration.pushManager.getSubscription()
  if (existing && sameKey(existing.options.applicationServerKey, applicationServerKey)) {
    return existing
  }
  if (existing) await existing.unsubscribe()
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey })
}

/** Whether a subscription was made for the given application server key. */
function sameKey(
  current: ArrayBuffer | null | undefined,
  expected: Uint8Array<ArrayBuffer>,
): boolean {
  if (!current) return false
  const bytes = new Uint8Array(current)
  if (bytes.length !== expected.length) return false
  return bytes.every((byte, i) => byte === expected[i])
}

/** Remove this browser's subscription. */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return
  const registration = await navigator.serviceWorker.getRegistration(SW_PATH)
  const subscription = await registration?.pushManager.getSubscription()
  if (subscription) {
    await api('/api/v1/me/push/subscriptions', {
      method: 'DELETE',
      query: { endpoint: subscription.endpoint },
    })
    await subscription.unsubscribe()
  }
  // Turn the channel off for this account too.
  await api('/api/v1/me/notification-prefs', {
    method: 'PATCH',
    body: { pushEnabled: false },
  })
}

/** Whether this browser currently holds a push subscription. */
export async function hasSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false
  const registration = await navigator.serviceWorker.getRegistration(SW_PATH)
  const subscription = await registration?.pushManager.getSubscription()
  return Boolean(subscription)
}
